import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Upload, Download, Trash2, Loader2, Link2 } from "lucide-react";
import { useDespachos } from "@/hooks/useDespachos";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

export const ClientFiles = ({ client }: { client: any }) => {
    const { files, fetchFiles, uploadFile, processes, updateClient } = useDespachos();
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        fetchFiles();
    }, []);

    if (!client) {
        return (
            <Card>
                <CardContent className="p-8 text-center text-gray-500">Cargando...</CardContent>
            </Card>
        );
    }

    // Obtener los datos del address serializado
    let extraData: any = {};
    if (client.address) {
        try {
            extraData = JSON.parse(client.address);
        } catch (e) {
            extraData = {};
        }
    }
    const jsonFileNames: string[] = extraData.fileNames || [];

    // 1. Obtener radicados de expedientes del cliente
    const clientRadicados = processes
        .filter(p => (p.client_name && p.client_name.trim().toLowerCase() === client.name?.trim().toLowerCase()) ||
                     (p.metadata && p.metadata.client_id === client.id))
        .map(p => p.case_number);

    // 2. Filtrar archivos que contengan el prefijo del cliente, radicado, o estén en jsonFileNames
    const clientFiles = files.filter(f => {
        const nameLower = f.name.toLowerCase();
        const clientPrefix = `[cliente-${client.name.toLowerCase().replace(/\s+/g, "_")}]`;
        const idPrefix = `[cliente-${client.id}]`;
        
        const matchesPrefix = nameLower.startsWith(clientPrefix) || nameLower.startsWith(idPrefix);
        const matchesRadicado = clientRadicados.some(rad => rad && nameLower.includes(rad.toLowerCase()));
        const matchesJson = jsonFileNames.includes(f.name);
        
        return matchesPrefix || matchesRadicado || matchesJson;
    });

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        try {
            // Guardamos con un prefijo explícito del cliente para vincularlo de por vida
            const clientPrefix = `[cliente-${client.name.toLowerCase().replace(/\s+/g, "_")}]`;
            const fullFileName = `${clientPrefix}_${file.name}`;
            await uploadFile(file, fullFileName);
            
            // Persistir también el nombre del archivo en extraData para vincularlo de por vida
            const updatedExtra = {
                ...extraData,
                fileNames: [...jsonFileNames, fullFileName]
            };
            await updateClient(client.id, {
                address: JSON.stringify(updatedExtra)
            });

            await fetchFiles();
            toast.success("Archivo subido con éxito");
        } catch (error) {
            console.error(error);
            toast.error("Error al subir el archivo");
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownload = async (fileName: string) => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data, error } = await supabase.storage
                .from('gestion_misarchivos')
                .download(`${user.id}/${fileName}`);

            if (error) throw error;

            const url = window.URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName.replace(/^\[cliente-[^\]]+\]_/, ""); // Remover prefijo al descargar
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error: any) {
            toast.error("Error al descargar", { description: error.message });
        }
    };

    const handleDelete = async (fileName: string) => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { error } = await supabase.storage
                .from('gestion_misarchivos')
                .remove([`${user.id}/${fileName}`]);

            if (error) throw error;

            toast.success("Archivo eliminado");
            await fetchFiles();
        } catch (error: any) {
            toast.error("Error al eliminar", { description: error.message });
        }
    };

    const formatSize = (bytes: number) => {
        if (!bytes) return "—";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    return (
        <Card className="border border-gray-100/80 shadow-sm overflow-hidden bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-3 bg-gray-50/50 border-b border-gray-100">
                <div>
                    <CardTitle className="text-base font-bold text-gray-900">Expedientes y Documentos</CardTitle>
                    <p className="text-xs text-gray-400">Archivos adjuntos asociados a los radicados o carpetas de {client.name}</p>
                </div>
                <div>
                    <input
                        type="file"
                        id="client-file-upload"
                        className="hidden"
                        onChange={handleUpload}
                        disabled={isUploading}
                    />
                    <label htmlFor="client-file-upload">
                        <Button
                            asChild
                            size="sm"
                            className="rounded-xl bg-legal-blue hover:bg-legal-navy gap-1.5 shadow-md cursor-pointer"
                            disabled={isUploading}
                        >
                            <span>
                                {isUploading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Upload className="h-4 w-4" />
                                )}
                                Subir Documento
                            </span>
                        </Button>
                    </label>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                {clientFiles.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow className="border-gray-100 hover:bg-transparent">
                                <TableHead className="font-semibold text-gray-500">Nombre del Archivo</TableHead>
                                <TableHead className="font-semibold text-gray-500">Tamaño</TableHead>
                                <TableHead className="font-semibold text-gray-500">Fecha Modificado</TableHead>
                                <TableHead className="font-semibold text-gray-500 text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {clientFiles.map((f) => (
                                <TableRow key={f.id} className="border-gray-100 hover:bg-blue-50/20 transition-colors">
                                    <TableCell className="font-semibold text-gray-700 text-xs flex items-center gap-2 py-3">
                                        <FileText className="h-4 w-4 text-legal-blue shrink-0" />
                                        <span className="truncate max-w-[300px]" title={f.name}>
                                            {f.name.replace(/^\[cliente-[^\]]+\]_/, "")}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-gray-500 text-xs">{formatSize(f.metadata?.size)}</TableCell>
                                    <TableCell className="text-gray-500 text-xs">
                                        {f.created_at ? new Date(f.created_at).toLocaleDateString() : "—"}
                                    </TableCell>
                                    <TableCell className="text-right space-x-1">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleDownload(f.name)}
                                            className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50 rounded-lg"
                                        >
                                            <Download className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleDelete(f.name)}
                                            className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 rounded-lg"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div className="text-center py-16 text-gray-400">
                        <Link2 className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                        <p className="text-sm">No hay archivos vinculados a este cliente.</p>
                        <p className="text-xs text-gray-300 mt-1">Sube un archivo usando el botón superior para vincularlo a su expediente.</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
